/**
 * Admin Statistics Page
 * ადმინისტრატორის სტატისტიკის გვერდი
 */

import { useState } from 'react';
import { Card, Spinner } from '@aiia/portal-core';
import {
  usePlatformOverview,
  useUserStatistics,
  useWidgetStatistics,
  useRevenueStatistics,
  useDataFilesStatistics,
  useSystemHealth,
} from '../../hooks/useAdminStatistics';
import type { StatisticsPeriod, ServiceHealth } from '../../features/api/adminStatistics';
import TrendLineChart from '../../components/admin/statistics/TrendLineChart';
import DistributionPieChart from '../../components/admin/statistics/DistributionPieChart';
import ChannelBarChart from '../../components/admin/statistics/ChannelBarChart';
import PeriodSelector from '../../components/admin/statistics/PeriodSelector';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('ka-GE', {
    style: 'currency',
    currency: 'GEL',
    minimumFractionDigits: 0,
  }).format(amount);
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
  loading,
  color = 'blue',
}: {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  loading?: boolean;
  color?: 'blue' | 'green' | 'purple' | 'orange';
}) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          {loading ? (
            <Spinner size="sm" className="mt-2" />
          ) : (
            <>
              <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
              {subtitle && (
                <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
              )}
            </>
          )}
        </div>
        <div className={`p-3 rounded-full ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

export default function StatisticsPage() {
  const [period, setPeriod] = useState<StatisticsPeriod>('MONTH');

  const { health, isLoading: loadingHealth } = useSystemHealth();
  const { overview, isLoading: loadingOverview } = usePlatformOverview();
  const { userStats, isLoading: loadingUsers } = useUserStatistics(period);
  const { widgetStats, isLoading: loadingWidgets } = useWidgetStatistics(period);
  const { revenueStats, isLoading: loadingRevenue } = useRevenueStatistics(period);
  const { dataFilesStats, isLoading: loadingDataFiles } = useDataFilesStatistics(period);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Statistics</h1>
          <p className="text-gray-600 mt-1">Overview of platform metrics and analytics</p>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {/* System Health */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">System Health</h2>
        {loadingHealth ? (
          <div className="flex justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {health?.services.map((service: ServiceHealth) => (
              <Card key={service.name} className="p-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-3 h-3 rounded-full flex-shrink-0 ${
                      service.status === 'UP'
                        ? 'bg-green-500'
                        : service.status === 'DOWN'
                          ? 'bg-red-500'
                          : 'bg-yellow-500'
                    }`}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {service.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span
                        className={`text-xs font-semibold ${
                          service.status === 'UP'
                            ? 'text-green-600'
                            : service.status === 'DOWN'
                              ? 'text-red-600'
                              : 'text-yellow-600'
                        }`}
                      >
                        {service.status}
                      </span>
                      {service.responseTimeMs > 0 && (
                        <span className="text-xs text-gray-400">
                          {service.responseTimeMs}ms
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={overview?.totalUsers || 0}
          subtitle={`+${overview?.newUsersThisMonth || 0} this month`}
          loading={loadingOverview}
          color="blue"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
        />
        <StatCard
          title="Active Widgets"
          value={overview?.activeWidgets || 0}
          subtitle={`${overview?.totalWidgets || 0} total`}
          loading={loadingOverview}
          color="green"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          }
        />
        <StatCard
          title="Conversations"
          value={overview?.totalConversations || 0}
          subtitle={`+${overview?.conversationsToday || 0} today`}
          loading={loadingOverview}
          color="purple"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
            </svg>
          }
        />
        <StatCard
          title="MRR"
          value={formatCurrency(revenueStats?.mrr || 0)}
          subtitle={`${revenueStats?.activeSubscriptions || 0} active`}
          loading={loadingRevenue}
          color="orange"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      {/* User Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          {loadingUsers ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : userStats?.registrationTrend && userStats.registrationTrend.length > 0 ? (
            <TrendLineChart
              data={userStats.registrationTrend}
              title="User Registrations"
              color="#3b82f6"
            />
          ) : (
            <div className="text-gray-500 text-center py-12">No registration data available</div>
          )}
        </Card>

        <Card className="p-6">
          {loadingUsers ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : userStats?.usersByStatus && Object.keys(userStats.usersByStatus).length > 0 ? (
            <DistributionPieChart
              data={userStats.usersByStatus}
              title="Users by Status"
            />
          ) : (
            <div className="text-gray-500 text-center py-12">No status data available</div>
          )}
        </Card>
      </div>

      {/* Widget Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          {loadingWidgets ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : widgetStats?.creationTrend && widgetStats.creationTrend.length > 0 ? (
            <TrendLineChart
              data={widgetStats.creationTrend}
              title="Widget Creation Trend"
              color="#10b981"
            />
          ) : (
            <div className="text-gray-500 text-center py-12">No widget creation data available</div>
          )}
        </Card>

        <Card className="p-6">
          {loadingWidgets ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : widgetStats?.widgetsByPosition && Object.keys(widgetStats.widgetsByPosition).length > 0 ? (
            <ChannelBarChart
              data={widgetStats.widgetsByPosition}
              title="Widgets by Position"
            />
          ) : (
            <div className="text-gray-500 text-center py-12">No position data available</div>
          )}
        </Card>
      </div>

      {/* Revenue Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          {loadingRevenue ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : revenueStats?.revenueTrend && revenueStats.revenueTrend.length > 0 ? (
            <TrendLineChart
              data={revenueStats.revenueTrend}
              title="Revenue Trend"
              color="#f59e0b"
            />
          ) : (
            <div className="text-gray-500 text-center py-12">No revenue data available</div>
          )}
        </Card>

        <Card className="p-6">
          {loadingRevenue ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : revenueStats?.subscriptionsByPlan && Object.keys(revenueStats.subscriptionsByPlan).length > 0 ? (
            <DistributionPieChart
              data={revenueStats.subscriptionsByPlan}
              title="Subscriptions by Plan"
            />
          ) : (
            <div className="text-gray-500 text-center py-12">No subscription data available</div>
          )}
        </Card>
      </div>

      {/* Data Files Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6">
          <h3 className="text-sm font-medium text-gray-600 mb-4">Storage Overview</h3>
          {loadingDataFiles ? (
            <Spinner size="lg" />
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Files</span>
                <span className="font-semibold">{dataFilesStats?.totalFiles || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Processed</span>
                <span className="font-semibold text-green-600">{dataFilesStats?.processedFiles || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Pending</span>
                <span className="font-semibold text-yellow-600">{dataFilesStats?.pendingFiles || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Failed</span>
                <span className="font-semibold text-red-600">{dataFilesStats?.failedFiles || 0}</span>
              </div>
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Storage</span>
                  <span className="font-semibold">{formatBytes(dataFilesStats?.totalStorageBytes || 0)}</span>
                </div>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6">
          {loadingDataFiles ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : dataFilesStats?.filesByType && Object.keys(dataFilesStats.filesByType).length > 0 ? (
            <DistributionPieChart
              data={dataFilesStats.filesByType}
              title="Files by Type"
              height={250}
            />
          ) : (
            <div className="text-gray-500 text-center py-12">No file type data available</div>
          )}
        </Card>

        <Card className="p-6">
          {loadingDataFiles ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : dataFilesStats?.uploadTrend && dataFilesStats.uploadTrend.length > 0 ? (
            <TrendLineChart
              data={dataFilesStats.uploadTrend}
              title="Upload Trend"
              color="#8b5cf6"
              height={250}
            />
          ) : (
            <div className="text-gray-500 text-center py-12">No upload data available</div>
          )}
        </Card>
      </div>
    </div>
  );
}
